import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ConnectionService } from '@features/pages/select-seat/services/connection.service';
import OktaAuth from '@okta/okta-auth-js';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ModalsService } from '../modals/modals.service';
import { GroupLinkService } from './group-link.service';

@Injectable({
  providedIn: 'root',
})
export class PrivateSellingService {
  public routeToNavigateBack: string = 'seat-selection';
  public ssoRoute: string = `${window.location.origin}/login`;
  public isEnabled = signal(false);
  public isLoginAttempted = signal(false);
  public isUserInWhitelist = signal(false);

  public readonly QUERY_PARAMS = {
    EVENT: 'event',
    STATE: 'state',
    IS_UNAUTHORIZED: 'isUnauthorized',
    IS_SIGN_IN_COMPLETE: 'isSignInComplete',
  };

  private router = inject(Router);
  private modalsService = inject(ModalsService);
  private connectionService = inject(ConnectionService);
  private groupLinkService = inject(GroupLinkService);

  public async authenticate(redirectDestination?: string) {
    const queryParams = new URLSearchParams(window.location.search);
    const stateParam = queryParams.get(this.QUERY_PARAMS.STATE) || '';
    const destination =
      redirectDestination || `${window.location.href}${stateParam}`;

    const client = this.initializeSsoClient();
    const isAuthenticated = await this.authenticateWithSso(client, destination);
    if (!isAuthenticated) return;

    const user = await client.getUser();
    await this.checkPatronIdInWhitelist(user.email as string);
  }

  public initializeSsoClient(): OktaAuth {
    return new OktaAuth({
      issuer: environment.oktaData.issuer,
      clientId: environment.oktaData.clientId,
      redirectUri: this.ssoRoute,
    });
  }

  public handleStateBackingFromMlb = (): void => {
    this.isEnabled.set(true);
    this.isLoginAttempted.set(true);
    this.isUserInWhitelist.set(false);
    this.modalsService.openForbiddenEventModal();
  };

  private async authenticateWithSso(
    authClient: OktaAuth,
    redirectDestination: string
  ): Promise<boolean> {
    let isAuthenticated = false;
    const queryParams = new URLSearchParams(window.location.search);
    const { IS_UNAUTHORIZED, IS_SIGN_IN_COMPLETE } = this.QUERY_PARAMS;

    const isOktaAuthenticated = await authClient.isAuthenticated();

    if (!isOktaAuthenticated) {
      const redirectUrl = new URL(redirectDestination);
      redirectUrl.searchParams.append(IS_SIGN_IN_COMPLETE, 'true');

      authClient.tokenManager.clear();
      authClient.setOriginalUri(redirectUrl.toString());
      await authClient.signInWithRedirect();
    } else if (queryParams.has(IS_UNAUTHORIZED)) {
      await authClient.signInWithRedirect();
    } else {
      isAuthenticated = true;
    }

    return isAuthenticated;
  }

  private async checkPatronIdInWhitelist(email: string): Promise<void> {
    try {
      await firstValueFrom(
        this.connectionService.getPatronIdFromWhitelist(email)
      );
      this.isUserInWhitelist.set(true);
    } catch {
      this.isUserInWhitelist.set(false);
      this.modalsService.openForbiddenEventModal();
    } finally {
      this.isLoginAttempted.set(true);
      this.navigateToSeatSelection();
    }
  }

  private navigateToSeatSelection() {
    this.router.navigate(
      this.groupLinkService.prependGroupLink([this.routeToNavigateBack]),
      {
        queryParams: {
          [this.QUERY_PARAMS.IS_SIGN_IN_COMPLETE]: undefined,
          [this.QUERY_PARAMS.EVENT]: this.connectionService.eventId.toString(),
        },
      }
    );
  }
}
